import { LitElement, html, css, nothing} from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { BestPracticeRoute, VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model';
import { DataExtractor } from './data-extractor';
import { debounce, passagePlanPropertiesOnRoute } from '../../../src/utils/helpers';
import { voyageRadioButtonsStyle, voyageCursorStyles } from '../../voyage-style/src/voyage-style';
import { RoutePassagePlanProperties } from '../../../src/models/voyage-routing-model';

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

@customElement("voyage-passage-plan-general")
export class VoyagePassagePlanGeneral extends LitElement
{
    @property({type: Boolean, attribute: false}) readOnly: boolean = false;
    @property({ type: Boolean }) isOfflinePlan: boolean = false;
    @property({ type: Boolean }) showPassagePlanFromPlan: boolean = false;

    constructor()
    {
        super();

        this._savePlan = this._savePlan.bind(this);
    }

    connectedCallback(): void {
        super.connectedCallback();
        VesselPlanningModel!.addEventListener('save-passage-plan', this._savePlan);
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        VesselPlanningModel!.removeEventListener('save-passage-plan', this._savePlan);
    }

    private _savePlan = () => {
        if (this.isOfflinePlan) {
            return;
        }
        
        if (VesselPlanningModel) // to avoid mocha failing to run tests
        {
            VesselPlanningModel.setPlan(this._plan!.scheduleId, this._plan!, false);    
        }
    }

    @property({type: Object, attribute: false})
    get plan()
    {
        return this._plan;
    }

    set plan(plan: VoyagePlan|null)
    {
        if (this._savingPlanCounter > 0)
        {
            // No need to update anything, the new plan is because we saved it
            this._savingPlanCounter--;
            return;
        }
        const old = this._plan;
        this._plan = plan;
        this._onNewPlan();
        this.requestUpdate('plan', old);
    }

    static get styles()
    {
        return [
            voyageRadioButtonsStyle,
            voyageCursorStyles,
            css`
            :host {
                font-family: MaerskText;
            }

            .container {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                margin: 20px;
                /* put in large margins to match the width of the other pages */
                margin-left: 337px;
                margin-right: 337px;
            }

            h2 {
                font-weight: bold;
                font-size: 16px;
                color: var(--neutrals-grey-02, black);
                margin-top: 30px;
                margin-bottom: 30px;
            }

            label, h3 {
                font-weight: normal;
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
                margin-bottom: 5px;
            }

            input[type="text"] {
                text-align: left;
                padding-left: 10px;
                padding-right: 10px;
                height: 52px;
                width: 486px;
                box-sizing: border-box;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 2.5px;
                color: var(--neutrals-grey-02, black);
                background-color: unset;
                font-size: 14px;
                font-family: MaerskText;
                margin-bottom: 30px;
            }

            input[type="text"]:focus-within {
                /* Drop Shadow Medium */
                box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06), 0px 2px 4px rgba(20, 20, 20, 0.1);
                outline: none;
            }

            input[type="text"].halfWidth {
                width: 230px;
            }

            input:disabled {
                background-color: var(--neutrals-grey-08, grey);
            }

            .doubleInputContainer {
                width: 486px;
                display: flex;
                flex-direction: row;
                justify-content: space-between;
            }

            .inputAndLabel {
                display: flex;
                flex-direction: column;
            }
            
            label.required::after {
                content: "*";
                color: red;
                margin-left: 4px;
                font-weight: bold;
            }

            .radioInputAndLabel {
                display: flex;
                flex-direction: row;
                align-items: center;
            }

            .radioInputAndLabel > * {
                cursor: pointer;
            }

            .radioInputAndLabel>label {
                margin-bottom: 0px;
                margin-left: 5px;
            }

            .radioInputAndLabel.last {
                margin-bottom: 30px;
            }

            textarea {
                text-align: left;
                padding-left: 10px;
                padding-right: 10px;
                height: 192px;
                width: 486px;
                box-sizing: border-box;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 2.5px;
                color: var(--neutrals-grey-02, black);
                background-color: unset;
                font-size: 14px;
                font-family: MaerskText;
                margin-bottom: 30px;
                resize: none;
            }

            textarea:focus-within {
                /* Drop Shadow Medium */
                box-shadow: 0px 4px 12px rgba(20, 20, 20, 0.06), 0px 2px 4px rgba(20, 20, 20, 0.1);
                outline: none;
            }

            textarea:disabled {
                background-color: var(--neutrals-grey-08, grey);
            }

            .dropdown {
                display: flex;
                flex-direction: column;
                width: 40px;
                box-sizing: border-box;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 2.5px;
                font-family: MaerskText;
                font-size: 14px;
                color: var(--neutrals-grey-02, black);
                background-color: var(--neutrals-grey-10, white);
                margin-bottom: 30px;
                padding-top: 7px;
                padding-left: 5px;
            }

            .dropdown.routereason {
                width: 311px;
                height: 40px;
            }

            .dropdown.routereason[invalid] {
                border: 1px solid var(--voyage-error-red, red);
            }

            textarea[invalid] {
                border: 1px solid var(--voyage-error-red, red);
            }

            .error-label {
                font-weight: normal;
                font-size: 12px;
                color: var(--voyage-error-red, red);
                margin-bottom: 5px;
                margin-top: -30px;
            }

            .planDate {
                border: 1px solid var(--functional-grey-400);
                text-align: left;
                padding-left: 10px;
                padding-right: 10px;
                height: 52px;
                width: 230px;
                box-sizing: border-box;
                border: 1px solid var(--neutrals-grey-06, grey);
                border-radius: 2.5px;
                color: var(--neutrals-grey-02, black);
                background-color: unset;
                font-size: 14px;
                font-family: MaerskText;
                margin-bottom: 30px;
            }
            
        `];
    }

    private _routeReason: string[] = ["Select reason for selected route",
                                        "Weather routing on AutoRoute",
                                        "Weather routing on own route",
                                        "Own route has better fuel consumption",
                                        "Own route has better weather conditions",
                                        "Own route has better traffic situation",
                                        "AutoRoute not found safe",
                                        "Other"];
    
    private _bestPracticeRouteCommentError: boolean = false;
    private _bestPracticeRouteReasonError: boolean = false;

    render()
    {
        const showBestPracticeRouteReason = this.plan && 
                                            this.plan.passagePlan.bestPracticeRoute && 
                                            this.plan.passagePlan.bestPracticeRoute!.available && 
                                            !this.plan.passagePlan.bestPracticeRoute!.selected;

        const currentSelectedRouteIndex = this.showPassagePlanFromPlan ? this.plan?.selectedRouteIndex :  this.plan?.routes.findIndex((route) => route.routeId === this.plan!.currentSelectedRouteId);

        const requiredClass = isVesselOnCloud ? "required" : "";

        return html`
            <div class="container" data-test="overview-content" @input=${this._onInput}>
                ${ showBestPracticeRouteReason ?
                html`
                <h2>REASON FOR YOUR SELECTED ROUTE</h2>
                <label for="reason">Reason</label>
                <select class="dropdown routereason" ?disabled=${this.readOnly}
                    .value=${this._plan!.passagePlan.bestPracticeRoute!.reason}
                    id="best-practice-route-routereason"
                    ?invalid=${this._bestPracticeRouteReasonError}
                    @input=${
                        (evt: InputEvent) => {
                            let bpr: BestPracticeRoute = JSON.parse(JSON.stringify(this.plan!.passagePlan.bestPracticeRoute!));
                            bpr.reason = (<HTMLTextAreaElement>evt.target).value;
                            this._plan!.passagePlan.bestPracticeRoute! = bpr;
                        }
                    }>
                    ${this._routeReason.map( (value) => html`
                    <option value=${value} ?selected=${ value === this._plan!.passagePlan.bestPracticeRoute!.reason}>${value}</option>
                    `)}
                    
                </select>
                ${ this._bestPracticeRouteReasonError ?
                html`
                    <div class="error-label">Please select reason</div>
                ` : html``}

                <label for="best-practice-route-comment">${this._plan!.passagePlan.bestPracticeRoute!.reason !== "Other" ? html`Comment (Optional)` : html`Comment`}</label>
                <textarea id="best-practice-route-comment" ?disabled=${this.readOnly} .value=${this._plan!.passagePlan.bestPracticeRoute!.comment || ''} 
                        ?invalid=${this._bestPracticeRouteCommentError}
                        placeholder="Write your reason and or notes"
                        @input=${
                        (evt: InputEvent) => {
                            let bpr: BestPracticeRoute = JSON.parse(JSON.stringify(this.plan!.passagePlan.bestPracticeRoute!));
                            bpr.comment = (<HTMLTextAreaElement>evt.target).value;
                            this._plan!.passagePlan.bestPracticeRoute! = bpr;
                        }
                    }>></textarea>` : html``}
                ${ this._bestPracticeRouteCommentError ?
                html`
                    <div class="error-label">Please provide a comment</div>
                ` : html``}

                <div class="doubleInputContainer">
                    <div class="inputAndLabel">
                        <label class=${requiredClass} for="departurePort">Departure Port</label>
                        <input id="departurePort" type="text" class="halfWidth" ?disabled=${this.readOnly}
                               .value=${DataExtractor.getDataFromPlan("departurePort", this._plan, this.showPassagePlanFromPlan) as string}>
                    </div>
                    <div class="inputAndLabel">
                        <label class=${requiredClass} for="arrivalPort">Arrival Port</label>
                        <input id="arrivalPort" type="text" class="halfWidth" ?disabled=${this.readOnly}
                               .value=${DataExtractor.getDataFromPlan("arrivalPort", this._plan, this.showPassagePlanFromPlan) as string}>
                    </div>
                </div>

                <h2>INFORMATION</h2>
                <label for="description" class=${requiredClass}>Description</label>
                <input id="description" type="text" ?disabled=${this.readOnly}
                       .value=${DataExtractor.getDataFromPlan("description", this._plan, this.showPassagePlanFromPlan) as string}>

                <div class="doubleInputContainer">
                    <div class="inputAndLabel">
                        <label for="departurePort" class=${requiredClass}>Departure Port</label>
                        <input id="departurePort" type="text" class="halfWidth" ?disabled=${this.readOnly}
                               .value=${DataExtractor.getDataFromPlan("departurePort", this._plan, this.showPassagePlanFromPlan) as string}>
                    </div>
                    <div class="inputAndLabel">
                        <label for="arrivalPort" class=${requiredClass}>Arrival Port</label>
                        <input id="arrivalPort" type="text" class="halfWidth" ?disabled=${this.readOnly}
                               .value=${DataExtractor.getDataFromPlan("arrivalPort", this._plan) as string}>
                    </div>
                </div>

                <label for="portMemo" class=${requiredClass}>Port Memo</label>
                <input id="portMemo" type="text" ?disabled=${this.readOnly} .value=${DataExtractor.getDataFromPlan("portMemo", this._plan, this.showPassagePlanFromPlan) as string}>

                <label for="avgSpeedOverGround" class=${requiredClass}>Average Speed Over Ground (Pilot/Pilot)</label>
                <input id="avgSpeedOverGround" type="text" class="halfWidth" readonly disabled .value=${DataExtractor.getDataFromPlan("avgSpeedOverGround", this._plan, this.showPassagePlanFromPlan) as string}>

                <label for="reminders">Reminders</label>
                <textarea id="reminders" ?disabled=${this.readOnly} .value=${DataExtractor.getDataFromPlan("reminders", this._plan, this.showPassagePlanFromPlan) as string}></textarea>

                <h2>CHARTS & LIGHTS</h2>

                <h3>Type of passage plan</h3>
                <div class="radioInputAndLabel">
                    <input id="radioECDISCompliant" type="radio" name="radioEcdis" ?disabled=${this.readOnly}
                           .checked=${this._plan ? !this._plan.routes[currentSelectedRouteIndex!]?.passagePlan!.notEcdisCompliant : true }>
                    <label for="radioECDISCompliant">Fully operational ECDIS with a full voyage of ENC charts</label>
                </div>
                <div class="radioInputAndLabel last">
                    <input id="radioNotECDISCompliant" type="radio" name="radioEcdis" ?disabled=${this.readOnly}
                           .checked=${this._plan ? this._plan.routes[currentSelectedRouteIndex!]?.passagePlan!.notEcdisCompliant! : false }>
                    <label for="radioNotECDISCompliant">Non-operational ECDIS or voyage with ports not covered by ENC</label>
                </div>

                <label for="paperCharts">Raster/Paper charts</label>
                <input id="paperCharts" type="text"
                       ?disabled=${this.readOnly || (this._plan && !this._plan.routes[currentSelectedRouteIndex!]?.passagePlan!.notEcdisCompliant)}
                       .value=${DataExtractor.getDataFromPlan("paperCharts", this._plan, this.showPassagePlanFromPlan) as string}>


                <label for="encCellsUsed">ENC Cells Used</label>
                <input id="encCellsUsed" type="text" ?disabled=${this.readOnly}
                       .value=${DataExtractor.getDataFromPlan("encCellsUsed", this._plan, this.showPassagePlanFromPlan) as string}>

                <label for="booksAndTables">Books & Tables</label>
                <textarea id="booksAndTables" ?disabled=${this.readOnly}
                          .value=${DataExtractor.getDataFromPlan("booksAndTables", this._plan, this.showPassagePlanFromPlan) as string}></textarea>

                <label for="chartsAndLightsNotes">Charts & Lights Notes</label>
                <textarea id="chartsAndLightsNotes" ?disabled=${this.readOnly}
                          .value=${DataExtractor.getDataFromPlan("chartsAndLightsNotes", this._plan, this.showPassagePlanFromPlan) as string}></textarea>

                <h2>PUBLICATIONS</h2>

                <label for="sailingDirections">Sailing directions (pilots) to be used on passage</label>
                <textarea id="sailingDirections" ?disabled=${this.readOnly}
                       .value=${DataExtractor.getDataFromPlan("sailingDirections", this._plan, this.showPassagePlanFromPlan) as string}></textarea>

                <div class="doubleInputContainer">
                    <div class="inputAndLabel">
                        <label for="listOfRadioSignals">List of radio signals for voyage</label>
                        <input id="listOfRadioSignals" type="text" class="halfWidth" ?disabled=${this.readOnly}
                               .value=${DataExtractor.getDataFromPlan("listOfRadioSignals", this._plan, this.showPassagePlanFromPlan) as string}>
                    </div>
                    <div class="inputAndLabel">
                        <label for="listOfLights">List of lights</label>
                        <input id="listOfLights" type="text" class="halfWidth" ?disabled=${this.readOnly}
                               .value=${DataExtractor.getDataFromPlan("listOfLights", this._plan, this.showPassagePlanFromPlan) as string}>
                    </div>
                </div>

                <label for="gpsEcdisRadarMap">GPS/ECDIS/Radar map</label>
                <input id="gpsEcdisRadarMap" type="text" ?disabled=${this.readOnly}
                       .value=${DataExtractor.getDataFromPlan("gpsEcdisRadarMap", this._plan, this.showPassagePlanFromPlan) as string}>

                <div class="doubleInputContainer">
                    <div class="inputAndLabel">
                        <label for="pubCorrectedToWeek">Pub. corrected to NTM week numb.</label>
                        <input id="pubCorrectedToWeek" type="text" class="halfWidth" ?disabled=${this.readOnly}
                               .value=${DataExtractor.getDataFromPlan("pubCorrectedToWeek", this._plan, this.showPassagePlanFromPlan) as string}>
                    </div>
                    <div class="inputAndLabel">
                        <label for="tideTables">Tide tables / program</label>
                        <input id="tideTables" type="text" class="halfWidth" ?disabled=${this.readOnly}
                               .value=${DataExtractor.getDataFromPlan("tideTables", this._plan, this.showPassagePlanFromPlan) as string}>
                    </div>
                </div>

                <label for="publicationsNotes">Publications Notes</label>
                <textarea id="publicationsNotes" ?disabled=${this.readOnly}
                          .value=${DataExtractor.getDataFromPlan("publicationsNotes", this._plan, this.showPassagePlanFromPlan) as string}></textarea>
                <h2>PASSAGE PLAN  PREPARED BY</h2>
                <div class="doubleInputContainer">
                    <div class="inputAndLabel">
                        <label for="captain">Captain</label>
                        <input id="captain" type="text" class="halfWidth" ?disabled=${this.readOnly}
                            .value=${DataExtractor.getDataFromPlan("captain", this._plan, this.showPassagePlanFromPlan) as string}>
                    </div>
                    <div class="inputAndLabel">
                        <label for="navigationalOfficer">Navigational Officer</label>
                        <input id="navigationalOfficer" type="text" class="halfWidth" ?disabled=${this.readOnly}
                            .value=${DataExtractor.getDataFromPlan("navigationalOfficer", this._plan, this.showPassagePlanFromPlan) as string}>
                    </div>
                </div>
                <div class="doubleInputContainer">
                    <div class="inputAndLabel">
                    <label for="Date">Date</label>
                    <input type="date"
                        id="planDate"
                        class="planDate"
                        .value=${DataExtractor.getDataFromPlan("planDate", this._plan, this.showPassagePlanFromPlan) as string}
                        ?disabled=${this.readOnly}    
                    />
                    </div>
                </div>
            </div>
        `;
    }

    private _plan: VoyagePlan|null = null;
    private _savingPlanCounter: number = 0;

    _onNewPlan()
    {
        if (this.isOfflinePlan) {
            return;
        }

        if (this._plan)
        {
            // Check if we need to update the plan with some auto-filled values
            let dirty = false;

            const description = <string>DataExtractor.getDataFromPlan("description", this._plan);
            if (this._plan.passagePlan.description !== description)
            {
                this._plan.passagePlan.description = description;
                dirty = true;
            }

            const departurePort = <string>DataExtractor.getDataFromPlan("departurePort", this._plan);
            if (this._plan.passagePlan.departurePort !== departurePort)
            {
                this._plan.passagePlan.departurePort = departurePort;
                dirty = true;
            }
            
            const arrivalPort = <string>DataExtractor.getDataFromPlan("arrivalPort", this._plan);
            if (this._plan.passagePlan.arrivalPort !== arrivalPort)
            {
                this._plan.passagePlan.arrivalPort = arrivalPort;
                dirty = true;
            }

            const portMemo = <string>DataExtractor.getDataFromPlan("portMemo", this._plan);
            if (this._plan.passagePlan.portMemo !== portMemo)
            {
                this._plan.passagePlan.portMemo = portMemo;
                dirty = true;
            }

            const sailingDirections = <string>DataExtractor.getDataFromPlan("sailingDirections", this._plan);
            if (this._plan.passagePlan.sailingDirections !== sailingDirections)
            {
                this._plan.passagePlan.sailingDirections = sailingDirections;
                dirty = true;
            }

            const pubCorrectedToWeek = <string>DataExtractor.getDataFromPlan("pubCorrectedToWeek", this._plan);
            if (this._plan.passagePlan.pubCorrectedToWeek !== pubCorrectedToWeek)
            {
                this._plan.passagePlan.pubCorrectedToWeek = pubCorrectedToWeek;
                dirty = true;
            }

            if (dirty)
            {
                this._savePlan();
            }
        }
    }

    async _onInput(evt: InputEvent)
    {
        const target = <HTMLInputElement|HTMLTextAreaElement>evt.target;
        const currentSelectedRouteIndex = this._plan!.routes.findIndex((route) => route.routeId === this._plan!.currentSelectedRouteId);
        if (target.id && target.id in this._plan!.passagePlan)
        {
            const propName = target.id;   

            if (passagePlanPropertiesOnRoute.includes(propName)) {
                if (propName == "planDate") {
                    this._plan!.routes[currentSelectedRouteIndex]!.passagePlan!.planDate = new Date(target.value).toISOString()
                } else {
                    (<string>this._plan!.routes[currentSelectedRouteIndex]!.passagePlan![<keyof RoutePassagePlanProperties>propName]) = target.value;
                }
            } else {
                if (propName === "planDate") {
                    this._plan!.passagePlan.planDate = new Date(target.value).toISOString()
                }else{
                    (<string>this._plan!.passagePlan[<keyof VoyagePlan["passagePlan"]>propName]) = target.value;
                }
            }
        }
        else if (target.id === "radioECDISCompliant" || target.id === "radioNotECDISCompliant")
        {
            this._plan!.routes[currentSelectedRouteIndex].passagePlan!.notEcdisCompliant = target.id === "radioNotECDISCompliant" && (<HTMLInputElement>target).checked;
            this.requestUpdate();
        }
        else if (target.id === "best-practice-route-routereason" || target.id === "best-practice-route-comment")
        {
            this._bestPracticeRouteCommentError = false;
            this._bestPracticeRouteReasonError = false;

            if (this.plan && 
                this.plan.passagePlan.bestPracticeRoute) {
                
                if (this.plan.passagePlan.bestPracticeRoute!.reason && 
                (this.plan.passagePlan.bestPracticeRoute!.reason === "Select reason for selected route" || this.plan.passagePlan.bestPracticeRoute!.reason === "")) {
                    this._bestPracticeRouteReasonError = true;
                }

                if (this.plan.passagePlan.bestPracticeRoute!.reason && 
                    this.plan.passagePlan.bestPracticeRoute!.reason === "Other" && 
                    (!this.plan.passagePlan.bestPracticeRoute!.comment || this.plan.passagePlan.bestPracticeRoute!.comment === "")) {
                        this._bestPracticeRouteCommentError = true;
                    }
            }

            this.dispatchEvent(new CustomEvent('plan-validation', { detail: { isValid: this._bestPracticeRouteCommentError || this._bestPracticeRouteReasonError} }));

            this.requestUpdate();
            if (!this._bestPracticeRouteCommentError && !this._bestPracticeRouteReasonError) {
                this._savePlan();
            }
        }
        VesselPlanningModel!._localPassageUpdated();
        
    }
}
